import type { NotificationSeverity, NotificationTab } from '../../../services/notificationsApi';

/**
 * How a notification is drawn: which icon, and in which tone.
 *
 * <h2>Why the icon is decided here and not by the server</h2>
 *
 * <p>An icon is a presentation choice, and `kind` is the server's name for what the notification is about.
 * Keeping the map on this side is what lets a new sender — a BID invitation, an approval request — reach
 * the bell with no server change at all: an unmapped kind falls back to the bell rather than breaking the
 * row, so the feature degrades to "drawn plainly" instead of to "crashed".
 */

/** `kind` is UPPER_SNAKE_CASE and owned by the sending module; these are the ones with an icon of their own. */
const ICON_BY_KIND: Record<string, string> = {
  /* Every kind the platform raises today, each matched to the reference's own choice of glyph. */
  MASTER_IMPORT: 'pi-database', //        ti-package / ti-database
  BID: 'pi-file', //                      ti-file-text
  BID_INVITATION: 'pi-file',
  BID_APPROVAL: 'pi-check-square', //     ti-file-text, on a step that is a decision
  BID_AWARD: 'pi-verified',
  TEMPLATE: 'pi-clone', //                ti-stack-2
  WORKFLOW: 'pi-sitemap', //              an approval route: steps branching in sequence
  PURCHASE_REQUEST: 'pi-list', //         ti-clipboard-list
  PURCHASE_ORDER: 'pi-shopping-cart', //  ti-file-invoice
  SUPPLIER_ONBOARDING: 'pi-building', //  ti-building-store
  USER_ACCESS: 'pi-user-edit', //         ti-user-cog
};

/** The bell itself: a notification whose kind this client has not been taught yet still has a row. */
const FALLBACK_ICON = 'pi-bell';

export function iconForKind(kind: string): string {
  const exact = ICON_BY_KIND[kind];
  if (exact !== undefined) return exact;
  /*
    Then by family, so BID_DEADLINE_EXTENDED draws a BID icon without this map having to list every event a
    module will ever raise. Keyed on the part before the first underscore -- `BID_APPROVAL` and `BID` are the
    same family, which the previous spelling missed: it tested `kind.startsWith('BID_')`, and a kind of
    exactly `BID` does not start with `BID_`, so the one the BID module actually sends fell through to the
    bell.
  */
  const family = kind.split('_')[0] ?? '';
  const match = Object.entries(ICON_BY_KIND).find(([known]) => known.split('_')[0] === family);
  return match?.[1] ?? FALLBACK_ICON;
}

/**
 * The BEM modifier for the row's tone.
 *
 * <p>A class rather than a colour, so the five tones are defined once in `layout.css` against the palette's
 * own tokens and move with the theme. A hex here would be wrong in six of the seven themes.
 */
export function toneOf(severity: NotificationSeverity): string {
  return `header__notify-icon--${severity.toLowerCase()}`;
}

/** The tabs, in the order the reference draws them, with the label each one shows. */
export const NOTIFICATION_TABS: { tab: NotificationTab; label: string }[] = [
  { tab: 'ALL', label: 'All' },
  { tab: 'UNREAD', label: 'Unread' },
  { tab: 'ACTION_REQUIRED', label: 'Action Required' },
];

/**
 * The heading over the empty state, which is the tab's own answer rather than one sentence for all three.
 *
 * <p>"No notifications yet" under the Unread tab would read as "you have none at all", which is usually
 * false and is the opposite of reassuring.
 */
export function emptyHeadingFor(tab: NotificationTab): string {
  switch (tab) {
    case 'UNREAD':
      return 'No unread notifications';
    case 'ACTION_REQUIRED':
      return 'Nothing needs your action';
    case 'ALL':
    default:
      return 'No notifications yet';
  }
}

/**
 * The line under the panel's title.
 *
 * <p>Two numbers when there is anything to report, and a plain reassurance when there is not — the
 * reference's own wording. "0 unread · 0 need your action" is a sentence that makes a reader check twice
 * to learn there is nothing to do.
 */
export function subtitleFor(unread: number, actionRequired: number): string {
  if (unread === 0 && actionRequired === 0) return "You're all caught up";
  const parts = [`${String(unread)} unread`];
  if (actionRequired > 0) parts.push(`${String(actionRequired)} need your action`);
  return parts.join(' · ');
}

/** The number a tab's label carries. */
export function countForTab(
  tab: NotificationTab,
  counts: { all: number; unread: number; actionRequired: number },
): number {
  switch (tab) {
    case 'UNREAD':
      return counts.unread;
    case 'ACTION_REQUIRED':
      return counts.actionRequired;
    case 'ALL':
    default:
      return counts.all;
  }
}
